import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { FlipAnimationService } from '@core/services/flip-animation.service';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { EEventType, EEventViewMode } from '@enums/event-tracking.enum';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FilterBadgeComponent } from '@shared/components/filter-badge/filter-badge.component';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { isNil } from 'lodash';
import { map, startWith } from 'rxjs';
import {
  IDeepLinkCreatorInfo,
  IDeepLinkDeliverablesFilter,
} from '../campaign-details-deep-link/campaign-details-deep-link.interface';
import { ECampaignDetailsTab, IAnalyticsFilterChangedEvent } from '../campaign-details.interface';
import { ICampaignExportConfig } from '../campaign-export-config/campaign-export-config.interface';
import { getDeepLinkMatchingTypes } from '../social-content-tiles-list/social-content-tiles-list.util';
import { CampaignDetailsDeliverablesFilterSelectComponent } from './campaign-details-deliverables-filter-select/campaign-details-deliverables-filter-select.component';
import { CampaignDetailsDeliverablesGroupToggleComponent } from './campaign-details-deliverables-group-toggle/campaign-details-deliverables-group-toggle.component';
import { CampaignDetailsDeliverablesStatusFiltersComponent } from './campaign-details-deliverables-status-filters/campaign-details-deliverables-status-filters.component';
import { CampaignDetailsDeliverablesTableComponent } from './campaign-details-deliverables-table/campaign-details-deliverables-table.component';
import {
  ICampaignDetailsDeliverablesFilterState,
  ICampaignDetailsDeliverablesGroupedParentRow,
  ICampaignDetailsDeliverablesTableRow,
  TCampaignDetailsDeliverablesGroupedRow,
} from './campaign-details-deliverables.interface';
import { CampaignDetailsDeliverablesService } from './campaign-details-deliverables.service';

@Component({
  imports: [
    CampaignDetailsDeliverablesFilterSelectComponent,
    CampaignDetailsDeliverablesGroupToggleComponent,
    CampaignDetailsDeliverablesStatusFiltersComponent,
    CampaignDetailsDeliverablesTableComponent,
    FilterBadgeComponent,
    TranslateModule,
  ],
  providers: [CampaignDetailsDeliverablesService, FlipAnimationService],
  selector: 'app-campaign-details-deliverables',
  templateUrl: './campaign-details-deliverables.component.html',
})
export class CampaignDetailsDeliverablesComponent {
  private readonly deliverablesService = inject(CampaignDetailsDeliverablesService);
  private readonly flipAnimation = inject(FlipAnimationService);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  private defaultMatchingApplied = false;
  private appliedDeepLinkRevision: number | null = null;

  public readonly analyticsNodes = input.required<IAnalyticsNode[] | null>();
  public readonly campaignExportConfig = input.required<ICampaignExportConfig>();
  public readonly loading = input<boolean>(true);
  public readonly deepLinkFilters = input<IDeepLinkDeliverablesFilter | null>(null);
  public readonly filteredCreator = input<IDeepLinkCreatorInfo | null>(null);
  public readonly hideTitleAndDescription = input<boolean>(false);

  /**
   * Passed straight through to the shared table (see its own contextColumn/storageKeyPrefix
   * doc-comments) - creator-details' Deliverables tab reuses this whole component, swapping the
   * table's Creator column for a Campaign one and namespacing its localStorage sort/pagination
   * state, while everything else here (filters, status filters, group toggle, row building)
   * behaves identically either way.
   */
  public readonly contextColumn = input<'creator' | 'campaign'>('creator');
  public readonly storageKeyPrefix = input<string>('campaign-details');
  /** Only set by creator-details' multi-campaign context - see buildRows' own doc-comment. */
  public readonly campaignNameById = input<Map<string, string> | undefined>(undefined);
  /** Only set by creator-details' multi-campaign context, same as campaignNameById above. */
  public readonly campaignIconById = input<Map<string, string | null | undefined> | undefined>(undefined);

  public readonly deliverableRowClicked = output<ICampaignDetailsDeliverablesTableRow>();
  public readonly deliverableRowGroupClicked = output<ICampaignDetailsDeliverablesGroupedParentRow>();
  /** See the table's own campaignClicked doc-comment - only ever fires for contextColumn 'campaign'. */
  public readonly campaignClicked = output<
    ICampaignDetailsDeliverablesGroupedParentRow | ICampaignDetailsDeliverablesTableRow
  >();
  public readonly filteredCreatorRemoved = output<void>();
  public readonly analyticsFilterChanged = output<IAnalyticsFilterChangedEvent>();

  private readonly tableRef = viewChild.required('tableRef', { read: ElementRef });

  private readonly effectiveAnalyticsNodes = computed(() => {
    const nodes = this.analyticsNodes();
    const creator = this.filteredCreator();

    if (!creator || !nodes) {
      return nodes;
    }

    return nodes.filter((node) => node.creatorId === creator.id);
  });

  public readonly filterState = signal<ICampaignDetailsDeliverablesFilterState>(
    this.deliverablesService.createInitialFilterState(),
  );
  public readonly groupByContent = signal<boolean>(true);

  constructor() {
    this.createDeepLinkFiltersEffect();
    this.createDefaultMatchingTypesEffect();
  }

  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  public readonly rows = computed((): ICampaignDetailsDeliverablesTableRow[] => {
    this.languageChange();

    return this.deliverablesService.buildRows(
      this.effectiveAnalyticsNodes(),
      this.translate,
      this.campaignNameById(),
      this.campaignIconById(),
    );
  });

  public readonly filterOptions = computed(() => {
    this.languageChange();

    return this.deliverablesService.buildFilterOptions(this.rows(), this.translate);
  });
  public readonly ctaTemplateEmptyLabel = computed((): string => {
    this.languageChange();

    return this.translate.instant('pages.campaignDetails.deliverables.filters.allDeliverableGroups', {
      number: this.filterOptions().ctaTemplateNames.length,
    }) as string;
  });
  public readonly deliverableTypeEmptyLabel = computed((): string => {
    this.languageChange();

    return this.translate.instant('pages.campaignDetails.deliverables.filters.allDeliverableTypes', {
      number: this.filterOptions().deliverableTypes.length,
    }) as string;
  });
  public readonly matchingEmptyLabel = computed((): string => {
    this.languageChange();

    return this.translate.instant('pages.campaignDetails.deliverables.filters.allMatchings', {
      number: this.filterOptions().matchingTypes.length,
    }) as string;
  });
  public readonly isContentLoading = computed((): boolean => {
    const analyticsNodes = this.analyticsNodes();

    return this.loading() || analyticsNodes === null;
  });

  public readonly filteredRows = computed(() => this.deliverablesService.filterRows(this.rows(), this.filterState()));

  private readonly statusFilteredRows = computed(() =>
    this.deliverablesService.filterRows(this.rows(), {
      ctaTemplateNames: this.filterState().ctaTemplateNames,
      deliverableTypes: this.filterState().deliverableTypes,
      matchingTypes: this.filterState().matchingTypes,
      statuses: new Set<EAnalyticsStatus>(),
    }),
  );

  public readonly statusOptions = computed(() =>
    this.deliverablesService.buildStatusOptions(this.statusFilteredRows(), this.filterState().statuses),
  );

  public readonly visibleStatusOptions = computed(() => {
    const allowed = this.campaignExportConfig().allowedDeliverableStatuses;
    const options = this.statusOptions();

    if (allowed === null) {
      return options;
    }

    return options.filter((o) => allowed.includes(o.key as EAnalyticsStatus));
  });

  public readonly groupedRows = computed<TCampaignDetailsDeliverablesGroupedRow[]>(() =>
    this.deliverablesService.buildGroupedRows(this.filteredRows()),
  );

  public onCtaTemplateNamesChanged(values: Set<string>): void {
    const previousValues = this.filterState().ctaTemplateNames;

    this.filterState.update((currentState) => ({
      ...currentState,
      ctaTemplateNames: values,
    }));

    this.emitFilterChanged('cta_template_names', previousValues, values);
  }

  public onDeliverableTypesChanged(values: Set<string>): void {
    const previousValues = this.filterState().deliverableTypes;

    this.filterState.update((currentState) => ({
      ...currentState,
      deliverableTypes: new Set(Array.from(values).map((value) => value as EDeliverableType)),
    }));

    this.emitFilterChanged('deliverable_types', previousValues, values);
  }

  public onGroupByContentChanged(value: boolean): void {
    this.groupByContent.set(value);
    this.flipAnimation.flip(this.tableRef().nativeElement as HTMLElement);

    this.eventTrackingService.track(EEventType.campaignAnalyticsGroupByContentChanged, {
      currentTab: toEventFrontendTab(ECampaignDetailsTab.deliverables),
      newValue: String(value),
      previousValue: String(!value),
      viewMode: value ? EEventViewMode.groupedByContent : EEventViewMode.list,
    });
  }

  public onMatchingTypesChanged(values: Set<string>): void {
    const previousValues = this.filterState().matchingTypes;

    this.filterState.update((currentState) => ({
      ...currentState,
      matchingTypes: new Set(Array.from(values).map((value) => value as EMatchingType)),
    }));

    this.emitFilterChanged('matching_types', previousValues, values);
  }

  public onStatusesChanged(values: Set<EAnalyticsStatus>): void {
    const previousValues = this.filterState().statuses;

    this.filterState.update((currentState) => ({
      ...currentState,
      statuses: values,
    }));

    this.emitFilterChanged('statuses', previousValues, values);
  }

  private emitFilterChanged(currentFilter: string, previousValues: Set<unknown>, currentValues: Set<unknown>): void {
    this.analyticsFilterChanged.emit({
      currentFilter,
      currentFilterValue: Array.from(currentValues).join(','),
      previousFilterValue: Array.from(previousValues).join(','),
    });
  }

  public onDeliverableRowClicked(row: ICampaignDetailsDeliverablesTableRow): void {
    this.deliverableRowClicked.emit(row);
  }

  private createDeepLinkFiltersEffect(): void {
    // A link resolves groups and matchings against the rows, so it waits until they are loaded, then applies once.
    effect(() => {
      const filters = this.deepLinkFilters();

      if (!filters || this.isContentLoading() || filters.revision === this.appliedDeepLinkRevision) {
        return;
      }

      this.appliedDeepLinkRevision = filters.revision;

      untracked(() => {
        const offeredMatchingTypes = this.filterOptions().matchingTypes.map((option) => option.value);
        const allowedStatuses = this.campaignExportConfig().allowedDeliverableStatuses;

        this.filterState.set({
          ...this.deliverablesService.createInitialFilterState(),
          ctaTemplateNames: this.deliverablesService.getCtaTemplateNames(this.rows(), filters.ctaTemplateIds),
          deliverableTypes: filters.deliverableTypes ?? new Set(),
          // Without any rows nothing is offered, so keep the link's types as they are.
          matchingTypes: offeredMatchingTypes.length
            ? getDeepLinkMatchingTypes(filters.matchingTypes, offeredMatchingTypes)
            : (filters.matchingTypes ?? new Set()),
          // A Shared Report hides some status chips; a hidden status would filter invisibly.
          statuses: new Set(
            [...(filters.statuses ?? [])].filter(
              (status) => allowedStatuses === null || allowedStatuses.includes(status),
            ),
          ),
        });
      });
    });
  }

  private createDefaultMatchingTypesEffect(): void {
    effect(() => {
      if (this.defaultMatchingApplied) {
        return;
      }

      const options = this.filterOptions().matchingTypes;

      if (!options.length) {
        return;
      }

      const hasUnrelated = options.some((o) => o.value === EMatchingType.unrelated);

      if (!hasUnrelated) {
        return;
      }

      // Deep-link or pre-set selections always win over defaults
      const hasDeepLinkMatchingTypes = !isNil(this.deepLinkFilters()?.matchingTypes);
      const hasExistingSelection = this.filterState().matchingTypes.size > 0;

      this.defaultMatchingApplied = true;

      if (hasDeepLinkMatchingTypes || hasExistingSelection) {
        return;
      }

      const defaults = new Set(options.map((o) => o.value).filter((v) => v !== EMatchingType.unrelated));

      untracked(() => {
        this.filterState.update((state) => ({ ...state, matchingTypes: defaults }));
      });
    });
  }
}
